import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigationItems = [
    { name: 'Home', path: '/homepage', icon: 'Home' },
    { name: 'About', path: '/about', icon: 'Info' },
    { name: 'Portfolio', path: '/portfolio', icon: 'Briefcase' },
    { name: 'Contact', path: '/contact', icon: 'Phone' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleQuoteRequest = () => {
    navigate('/contact');
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-background/95 backdrop-blur-sm shadow-lg' : 'bg-background shadow-sm'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-20 lg:h-24 px-4 sm:px-6 lg:px-8">
          {/* Logo Section */}
          <div 
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={() => handleNavigation('/homepage')}
          >
            <img 
              src="/assets/images/logo.png" 
              alt="Kunjvihari Impex" 
              className="h-24 lg:h-28 w-auto" 
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`nav-link flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-md text-base xl:text-lg font-semibold transition-all duration-300 ${
                  isActivePath(item?.path) 
                    ? 'text-primary bg-primary/10' 
                    : 'text-text-secondary hover:text-primary hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={18} className="xl:w-5 xl:h-5" />
                <span>{item?.name}</span>
              </button>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 lg:space-x-3 xl:space-x-4">
            {/* Desktop CTA Button */}
            <Button
              variant="default"
              size="sm"
              className="hidden lg:flex btn-textile text-sm xl:text-base font-semibold px-4 xl:px-6"
              onClick={handleQuoteRequest}
              iconName="MessageSquare"
              iconPosition="left"
              iconSize={16}
            >
              Get Quote
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md text-text-secondary hover:text-primary hover:bg-muted transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className={`lg:hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen 
            ? 'max-h-96 opacity-100' 
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="px-4 py-4 bg-surface border-t border-border">
            <nav className="space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-semibold text-left transition-all duration-200 ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-secondary hover:text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.name}</span>
                </button>
              ))}
              
              <div className="pt-3 border-t border-border mt-3">
                <Button
                  variant="default"
                  fullWidth
                  size="md"
                  className="btn-textile font-semibold"
                  onClick={handleQuoteRequest}
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={18}
                >
                  Get Custom Quote
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </div>
      
      {/* Bottom Border */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
    </header>
  );
};

export default Header;